import { createRouter, createWebHistory } from 'vue-router';
import { permissionFor } from '@/lib/nav';
import { useAuthStore } from '@/stores/auth';
import { reportModuleLoadError } from '@/lib/module-recovery';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { public: true },
    },
    {
      path: '/',
      name: 'grid',
      component: () => import('@/views/GridView.vue'),
    },
    {
      // Карточка открывается поверх грида как выезжающая панель,
      // но у неё есть собственный адрес — ссылку можно переслать коллеге
      path: '/candidates/:id',
      name: 'candidate',
      component: () => import('@/views/GridView.vue'),
      props: true,
    },
    {
      // Остальные вкладки рабочей книги (Р-22). Какие листы есть —
      // решает база, поэтому маршрут один на все
      path: '/sheets/:code',
      name: 'sheet',
      component: () => import('@/views/SheetView.vue'),
      meta: { permission: permissionFor('sheet') },
    },
    { path: '/sheets/blacklist', redirect: '/blacklist' },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('@/views/DashboardView.vue'),
      meta: { permission: permissionFor('dashboard') },
    },
    {
      path: '/import',
      name: 'import',
      component: () => import('@/views/ImportView.vue'),
      meta: { permission: permissionFor('import') },
    },
    {
      path: '/mailing',
      name: 'mailing',
      component: () => import('@/views/MailingView.vue'),
      meta: { permission: permissionFor('mailing') },
    },
    {
      path: '/users',
      name: 'users',
      component: () => import('@/views/UsersView.vue'),
      meta: { permission: permissionFor('users') },
    },
    {
      /*
        Раздел стоит в адресе, а не в состоянии компонента.

        Без него F5 и «Назад» всегда возвращали на «Статусы»: активная
        вкладка жила локальным ref. При этом решения Р-26 и Р-27 называют
        «Настройки → Оформление» местом настройки — дать этот адрес
        ссылкой было нечем, а из меню колонки некуда было вести.

        Параметр необязательный: `/settings` по-прежнему открывается
        и показывает первый доступный раздел.
      */
      path: '/settings/:section?',
      name: 'settings',
      component: () => import('@/views/SettingsView.vue'),
      meta: { permission: permissionFor('settings') },
    },
    {
      path: '/blacklist',
      name: 'blacklist',
      component: () => import('@/views/BlacklistView.vue'),
      meta: { permission: permissionFor('blacklist') },
    },
    {
      // Личный питомец доступен всем вошедшим, в том числе при выключенном интерактиве.
      path: '/profile/pet',
      name: 'pet',
      component: () => import('@/views/PetView.vue'),
    },
    {
      path: '/profile/pet/yard',
      name: 'pet-yard',
      component: () => import('@/views/PetYardView.vue'),
    },
    {
      // Личная история доступна каждому вошедшему; общая лента проверяет audit.view.
      path: '/activity',
      name: 'activity',
      component: () => import('@/views/ActivityView.vue'),
    },
    {
      path: '/journal',
      name: 'journal',
      component: () => import('@/views/JournalView.vue'),
      meta: { permission: permissionFor('journal') },
    },
    {
      // Витрина компонентов shadcn-vue — для разработки, не для заказчика
      path: '/ui-kit',
      name: 'ui-kit',
      component: () => import('@/components/ui/__showcase.vue'),
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/',
    },
  ],
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();

  // Первый заход: сессия ещё не проверена — иначе перезагрузка страницы
  // всегда выкидывала бы на экран входа
  if (!auth.checked) await auth.fetchMe();

  if (to.meta.public) {
    return auth.isAuthenticated && to.name === 'login' ? { name: 'grid' } : true;
  }

  if (!auth.isAuthenticated) {
    return { name: 'login', query: to.fullPath !== '/' ? { redirect: to.fullPath } : undefined };
  }

  /*
    Раздела нет в меню — значит, его нет и по адресу.

    Сервер и так отвечает «недостаточно прав» на каждый запрос: увести
    отсюда — не защита, а честность интерфейса. Без этого рекрутёр,
    набравший /users руками или перешедший по чужой ссылке, попадал на
    пустую страницу с красной плашкой и решал, что что-то сломалось.
  */
  const need = permissionFor(String(to.name ?? ''));
  if (need && !auth.can(need)) return { name: 'grid' };

  return true;
});

/**
 * Уход с настроек справочников сбрасывает их кэш.
 *
 * Строки грида приходят с сервера уже с названием и цветом статуса,
 * поэтому таблица обновляется сама. А вот выпадающие списки редактора
 * ячейки берут значения из кэша стора — без сброса они показывали бы
 * старое название до перезагрузки страницы.
 */
router.afterEach((_to, from) => {
  if (from.name === 'settings') {
    void import('@/stores/dictionaries').then(({ useDictionariesStore }) => {
      useDictionariesStore().invalidate();
    }).catch((error: unknown) => {
      if (!reportModuleLoadError(error)) console.error(error);
    });
  }
});

export default router;
